import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useSearchParams } from "react-router-dom";

import "../../assets/css/product-studio-edit.css";
import "../../assets/css/analytics.css";
import { useFetchCompanyUserDataQuery } from "../../api/usersApi";
import { useLanguage } from "../../contexts/LanguageContext";
import { useTranslation } from "../../hooks/useTranslation";
import { Btn, Icon, Segmented, Select, TextInput, cx } from "../catalogProductStudio/edit/StudioUi";
import {
  DATE_PRESETS,
  canSeeCost,
  fill,
  formatDate,
  presetRange,
  readRangeState,
  todayKey,
  validateRange,
  writeRangeState,
} from "./analyticsModel";
import InsightsSection from "./sections/InsightsSection";
import InventorySection from "./sections/InventorySection";
import OverviewSection from "./sections/OverviewSection";
import ProductsSection from "./sections/ProductsSection";
import ProfitabilitySection from "./sections/ProfitabilitySection";
import ReportsSection from "./sections/ReportsSection";
import MetaAdsSection from "./sections/MetaAdsSection";
import TrafficSection from "./sections/TrafficSection";
import SalesSection from "./sections/SalesSection";

export const ANALYTICS_SECTIONS = [
  { key: "overview", path: "/admin/analytics", icon: "grid", Component: OverviewSection, granularity: true },
  { key: "traffic", path: "/admin/analytics/traffic", icon: "trendUp", Component: TrafficSection, snapshot: true },
  { key: "meta-ads", path: "/admin/analytics/meta-ads", icon: "trendUp", Component: MetaAdsSection, snapshot: true },
  { key: "sales", path: "/admin/analytics/sales", icon: "cart", Component: SalesSection, granularity: true },
  { key: "products", path: "/admin/analytics/products", icon: "tag", Component: ProductsSection },
  { key: "inventory", path: "/admin/analytics/inventory", icon: "cube", Component: InventorySection, snapshot: true },
  { key: "profitability", path: "/admin/analytics/profitability", icon: "lock", Component: ProfitabilitySection, granularity: true, costOnly: true },
  { key: "insights", path: "/admin/analytics/insights", icon: "sparkle", Component: InsightsSection },
  { key: "reports", path: "/admin/analytics/reports", icon: "printer", Component: ReportsSection },
];

const RangeControls = ({ state, onChange, tt, lang, showGranularity }) => {
  const [draft, setDraft] = useState({ dateFrom: state.dateFrom, dateTo: state.dateTo });
  useEffect(() => setDraft({ dateFrom: state.dateFrom, dateTo: state.dateTo }), [state.dateFrom, state.dateTo]);
  const error = validateRange(draft);
  const dirty = draft.dateFrom !== state.dateFrom || draft.dateTo !== state.dateTo;

  return (
    <form
      className="an-range an-noprint"
      onSubmit={(event) => {
        event.preventDefault();
        if (!error) onChange({ preset: "custom", ...draft, granularity: state.granularityExplicit ? state.granularity : undefined });
      }}
    >
      <label className="an-range__field">
        <span>{tt("range")}</span>
        <Select
          value={state.preset}
          onChange={(preset) => {
            if (preset === "custom") return;
            onChange({ preset, ...presetRange(preset, todayKey()), granularity: undefined });
          }}
        >
          {[...DATE_PRESETS, "custom"].map((preset) => (
            <option key={preset} value={preset}>
              {tt(`preset_${preset}`)}
            </option>
          ))}
        </Select>
      </label>
      <label className="an-range__field">
        <span>{tt("dateFrom")}</span>
        <TextInput type="date" value={draft.dateFrom} max={draft.dateTo} onChange={(value) => setDraft((current) => ({ ...current, dateFrom: value }))} />
      </label>
      <label className="an-range__field">
        <span>{tt("dateTo")}</span>
        <TextInput type="date" value={draft.dateTo} min={draft.dateFrom} max={todayKey()} onChange={(value) => setDraft((current) => ({ ...current, dateTo: value }))} />
      </label>
      <Btn type="submit" variant={dirty ? "primary" : "secondary"} disabled={Boolean(error) || !dirty}>
        {tt("apply")}
      </Btn>
      {showGranularity ? (
        <div className="an-range__field">
          <span>{tt("granularity")}</span>
          <Segmented
            ariaLabel={tt("granularity")}
            value={state.granularity}
            onChange={(granularity) => onChange({ ...state, granularity })}
            options={["day", "week", "month"].map((value) => ({ value, label: tt(`g_${value}`) }))}
          />
        </div>
      ) : null}
      {error && dirty ? (
        <p className="an-range__error" role="alert">
          {tt(`rangeError_${error}`)}
        </p>
      ) : (
        <p className="an-range__summary">
          {fill(tt("periodLabel"), { from: formatDate(state.dateFrom, lang), to: formatDate(state.dateTo, lang), days: state.days })} · {tt("timezoneNote")}
        </p>
      )}
    </form>
  );
};

function Analytics({ section = "overview" }) {
  const t = useTranslation();
  const tt = useCallback((key) => t(`analytics.${key}`), [t]);
  const { language } = useLanguage();
  const lang = language === "en" ? "en" : "sq";
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: companyUser } = useFetchCompanyUserDataQuery();
  const costVisible = canSeeCost(companyUser?.user?.role);

  const state = useMemo(() => readRangeState(searchParams, todayKey()), [searchParams]);
  const current = ANALYTICS_SECTIONS.find((item) => item.key === section) || ANALYTICS_SECTIONS[0];
  const SectionComponent = current.Component;
  const search = searchParams.toString() ? `?${searchParams.toString()}` : "";

  const onRangeChange = (next) => setSearchParams(writeRangeState(searchParams, next), { replace: false });

  return (
    <div className="content">
      <div className="pse an">
        <header className="an-head an-noprint">
          <div>
            <p className="an-eyebrow">
              <Icon name="trendUp" size={14} /> {tt("eyebrow")}
            </p>
            <h1 className="an-head__title">{tt(section === "meta-ads" ? "tab_metaAds" : section === "traffic" ? "tab_traffic" : section === "overview" ? "salesIntelligenceTitle" : "pageTitle")}</h1>
            <p className="an-head__subtitle">{tt(section === "meta-ads" ? "meta_subtitle" : section === "traffic" ? "traffic_subtitle" : "pageSubtitle")}</p>
          </div>
        </header>

        <nav className="an-tabs an-noprint" aria-label={tt("sectionsLabel")}>
          {ANALYTICS_SECTIONS.filter((item) => !item.costOnly || costVisible).map((item) => (
            <NavLink
              key={item.key}
              to={`${item.path}${search}`}
              end
              className={({ isActive }) => cx("an-tab", isActive && "is-active")}
            >
              <Icon name={item.icon} size={16} />
              <span>{tt(`tab_${item.key}`)}</span>
            </NavLink>
          ))}
        </nav>

        {current.snapshot ? null : (
          <RangeControls state={state} onChange={onRangeChange} tt={tt} lang={lang} showGranularity={current.granularity} />
        )}

        <div className="an-body">
          <SectionComponent range={state} tt={tt} lang={lang} costVisible={costVisible} />
        </div>
      </div>
    </div>
  );
}

export default Analytics;
